import { useQuery } from "@tanstack/react-query";

import { backofficeClient } from "@/lib/api/backoffice-client";
import type { DeviceSignal } from "@/lib/solution-engineer/integration-health";

/** integrations credentials list via the backoffice proxy; carries no secrets. */
const CREDENTIALS_PATH = "/proxy/integrations/api/v1/credentials";
/** asset-management-api listAssets. */
const ASSETS_PATH = "/proxy/asset-management/api/v1/assets";

/** Mirrors integrations CredentialSummary. */
export interface TenantIntegration {
  id: string;
  name: string;
  provider: string;
  category: string;
  label: string | null;
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

export function useTenantIntegrations(tenantId: string) {
  return useQuery({
    queryKey: ["solution-engineer", "integrations", tenantId],
    queryFn: async () => {
      const { data } = await backofficeClient.get<{
        credentials: TenantIntegration[];
      }>(CREDENTIALS_PATH, {
        params: { tenant_id: tenantId, include_disabled: true },
      });
      return data.credentials;
    },
    enabled: Boolean(tenantId),
  });
}

/**
 * How many assets an MDM/EDR integration has and its most recent device
 * check-in: the newest `last_seen` (the API sorts nulls last).
 */
export function useDeviceSignal(tenantId: string, integrationId: string) {
  return useQuery({
    queryKey: ["solution-engineer", "device-signal", tenantId, integrationId],
    queryFn: async (): Promise<DeviceSignal> => {
      const { data } = await backofficeClient.get<{
        total_count: number;
        items: { last_seen?: string | null }[];
      }>(ASSETS_PATH, {
        params: {
          tenant_id: tenantId,
          integration_id: integrationId,
          sort_by: "last_seen",
          sort_direction: "desc",
          limit: 1,
        },
      });
      return {
        total: data.total_count,
        latestCheckIn: data.items.at(0)?.last_seen ?? null,
      };
    },
  });
}
